<script setup>
import { computed, nextTick, ref } from 'vue'
import { useStore } from 'vuex'
import QualityTypeSelector from '../Common/QualityTypeSelector.vue'
import PackagingTypeSelector from '../Common/PackagingTypeSelector.vue'
import GramajeField from '../Common/GramajeField.vue'
import ProcessCommodityTable from '../Process/ProcessCommodityTable.vue'
import { positiveInteger } from '../../services/process/ProcessMetrics'
const props = defineProps({ items: { type: Array, default: () => [] }, readonly: Boolean })
const emit = defineEmits(['update:modelValue'])
const store = useStore()
const dialog = ref(false)
const deleting = ref(null)
const form = ref(null)
const saving = ref(false)
const editedId = ref(null)
const draft = ref({})
const error = ref('')
const required = value => Boolean(value?.trim()) || 'Campo requerido.'
const title = computed(() => editedId.value == null ? 'Agregar mercadería procesada' : 'Editar mercadería procesada')

async function open (item = null) {
  error.value = ''
  editedId.value = item?.id ?? null
  if (!item) await store.dispatch('typesModule/getPackagingTypes')
  const bigBag = (store.getters['typesModule/getPackagingTypes'] || []).find(pack =>
    pack.packagingType?.replace(/[ _-]/g, '').toUpperCase() === 'BIGBAG')
  draft.value = item ? JSON.parse(JSON.stringify(item)) : {
    id: Math.min(0, ...props.items.map(row => row.id || 0)) - 1,
    qualityType: null, packagingType: bigBag || null, bagQuantity: 1,
    amount: '', locationInPlant: '', gramaje: null, obs: '', editable: true
  }
  dialog.value = true
  await nextTick()
  form.value?.resetValidation()
}
async function save (another = false) {
  if (saving.value) return
  saving.value = true
  try {
    if (!(await form.value.validate()).valid) return
    const item = { ...draft.value, amount: Number(draft.value.amount), bagQuantity: Number(draft.value.bagQuantity),
      gramaje: draft.value.gramaje == null ? null : Number(draft.value.gramaje),
      locationInPlant: draft.value.locationInPlant.trim() }
    emit('update:modelValue', editedId.value == null ? [...props.items, item]
      : props.items.map(row => row.id === editedId.value ? item : row))
    dialog.value = false
    if (another) {
      await nextTick()
      await open()
    }
  } finally { saving.value = false }
}
function remove () {
  emit('update:modelValue', props.items.filter(row => row.id !== deleting.value.id))
  deleting.value = null
}
function updateRows (items) { emit('update:modelValue', items) }
</script>

<template>
  <div>
    <div class="d-flex align-center flex-wrap ga-3">
      <h2 class="text-h6">Mercadería procesada</h2>
      <v-spacer />
      <v-btn v-if="!readonly" color="primary" prepend-icon="add" @click="open()">Agregar resultado</v-btn>
    </div>
    <ProcessCommodityTable :items="items" :editable="!readonly" @update:items="updateRows">
      <template #actions="{ item }">
        <v-btn icon="edit" size="small" variant="text" aria-label="Editar resultado" @click="open(item)" />
        <v-btn icon="delete" size="small" variant="text" color="error" aria-label="Quitar resultado" @click="deleting = item" />
      </template>
    </ProcessCommodityTable>
    <v-dialog v-model="dialog" max-width="760" scrollable>
      <v-card>
        <v-card-title>{{ title }}</v-card-title>
        <v-card-text>
          <v-alert v-if="error" type="error">{{ error }}</v-alert>
          <v-form ref="form" @submit.prevent="save(false)">
            <QualityTypeSelector v-model="draft.qualityType" />
            <v-row class="mt-1">
              <v-col cols="12" md="6"><v-text-field v-model="draft.amount" label="Kilogramos" type="number" min="1" step="1" :rules="[positiveInteger]" /></v-col>
              <v-col cols="12" md="6"><v-text-field v-model="draft.locationInPlant" label="Ubicación en planta" :rules="[required, v => v.length <= 128 || 'Máximo 128 caracteres.']" /></v-col>
              <v-col cols="12" md="6"><PackagingTypeSelector v-model="draft.packagingType" /></v-col>
              <v-col cols="12" md="6"><v-text-field v-model="draft.bagQuantity" label="Cantidad de bultos" type="number" min="1" step="1" :rules="[positiveInteger]" /></v-col>
              <v-col cols="12"><GramajeField v-model="draft.gramaje" /></v-col>
              <v-col cols="12"><v-textarea v-model="draft.obs" label="Observaciones" rows="2" auto-grow /></v-col>
            </v-row>
            <v-btn type="submit" color="primary" :loading="saving">{{ editedId == null ? 'Agregar' : 'Guardar resultado' }}</v-btn>
            <v-btn v-if="editedId == null" class="ml-2" variant="tonal" :disabled="saving" @click="save(true)">Agregar y cargar otro</v-btn>
          </v-form>
        </v-card-text>
        <v-card-actions><v-btn variant="text" :disabled="saving" @click="dialog = false">Salir sin agregar</v-btn></v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog :model-value="deleting != null" max-width="480" @update:model-value="deleting = null">
      <v-card title="Quitar resultado">
        <v-card-text>Se quitará esta fila del borrador. El cambio se confirma al guardar el parte.</v-card-text>
        <v-card-actions><v-btn @click="deleting = null">Volver</v-btn><v-btn color="error" @click="remove">Quitar</v-btn></v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
